import React, { useEffect } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'
import { InputPassword, useUncontrolled } from '@overdose/components'
import { IconEye, IconEyeOff } from '@tabler/icons-react'
import classNames from 'classnames'
import { usePage } from '~/hooks'
import { Button } from '../Button'
import { Icon } from '../Icon'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './PasswordInput.module.css'
import { PasswordInputProps } from './PasswordInput.types'

const PasswordInput = ({
  label,
  name,
  register,
  error,
  visibility,
  defaultVisibility = false,
  onVisibilityChange,
  ...props
}: PasswordInputProps) => {
  let useFormProps = {} as UseFormRegisterReturn
  if (register) {
    useFormProps = register(name)
  }

  const [visible, handleChange] = useUncontrolled({
    value: visibility,
    defaultValue: defaultVisibility || false,
    onChange: onVisibilityChange,
  })

  useEffect(() => {
    window.dispatchEvent(new Event('resize'))
  }, [error])

  const handleMouseEvents = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
  }

  const page = usePage()
  const isRTM = page.brand === 'rtm'

  return (
    <div>
      {label && (
        <label htmlFor={`${name}_input_password`} className='inline-block mb-2'>
          <Typography
            tag={TypographyTag.span}
            variant={
              isRTM
                ? TypographyVariant.BodyRegularBold
                : TypographyVariant.BodySmallBold
            }
            className={classNames('inline-block text-primary-heading', {
              [styles.disabledLabel]: props.disabled,
            })}>
            {label}
            {props.required && '*'}
          </Typography>
        </label>
      )}
      <InputPassword
        {...props}
        visibility={visible}
        name={name}
        {...useFormProps}
        error={error}
        theme={{
          ...styles,
          inner: classNames(styles?.inner, {
            [styles.inputError]: error,
          }),
        }}
        suffix={
          <Button
            disabled={props.disabled}
            style={{ '--btn-icon-size': '20px', '--input-stroke-width': '2px' }}
            title={visible ? 'Hide Password' : 'Show Password'}
            flat
            shape='circle'
            size='xs'
            variant='ghost'
            onClick={() => {
              handleChange?.(!visible)
            }}
            onMouseDown={handleMouseEvents}
            onMouseUp={handleMouseEvents}
            iconOnly
            icon={visible ? <IconEye /> : <IconEyeOff />}
          />
        }
        onChange={(
          value: string,
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          if (props?.onChange) {
            props?.onChange(value, event)
          } else if (Object.keys(useFormProps).length > 0) {
            useFormProps.onChange(event)
          }
        }}
      />
      {error && (
        <div className='flex gap-2 mt-2 items-center'>
          <Icon
            iconName='alert-triangle-filled'
            size={16}
            color='var(--color-state-alert)'
          />
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodySmallBold}
            className={styles.validationError}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  )
}

export { PasswordInput }
